// BulkPhotoAttachModal — put a folder of site photos back against the items
// they belong to, in one pass.
// ---------------------------------------------------------------------------
// 2026-08-20. Brett pinned 25 defects on a site with no reception and
// photographed every one. A bug dropped the photos (fixed — see
// outbox-refs.js), and he was left with a report he called worthless without
// images and no way to fix it short of reopening 25 items one at a time.
//
// So: pick the photos, they land against the items in the order they were
// taken, you check the grid, you change any that are wrong, and it uploads.
// You walk a site in one direction — pin, photograph, move on — so that order
// is right far more often than it is wrong, and where it is wrong it is
// obvious from the thumbnail.
//
// NOTHING UPLOADS UNSEEN. The plan line says exactly what is about to happen,
// including how many items will still have no photo when it finishes. A silent
// mismatch here would recreate the original failure in a new costume.
//
// Uploads go through the OUTBOX, not straight to the API — this is a phone on
// a site, and the queue is what makes twenty-five uploads survive a tunnel.

import React, { useMemo, useRef, useState } from 'react';
import { theme, btnPrimary, btnSecondary } from '../theme';
import { Images, X } from 'lucide-react';
import { assignInOrder, repoint, describePlan } from '../utils/bulkPhotoAssign';
import { addToOutbox, drainOutbox } from '../utils/outbox.js';
// v0.171.0 — photos are queued as Blobs (capped original + variants), not as
// base64 strings. See imageVariants.js / outbox.js.
import { makePhotoBlobs } from '../utils/imageVariants';
// 2026-09-15 — iPhone photos (HEIC) become JPEG when they are picked.
import { mayBePhoto, toBrowserImages } from '../utils/heicPhotos';

export default function BulkPhotoAttachModal({ defects = [], onClose, onDone }) {
  const fileRef = useRef(null);
  const [picked, setPicked] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [extraPhotos, setExtraPhotos] = useState([]);
  const [unphotographed, setUnphotographed] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);
  const [converting, setConverting] = useState(false);

  // Object URLs for the thumbnails. Revoked on close rather than per render —
  // twenty-five full-size photos held twice is what makes a phone drop the tab.
  const previews = useMemo(() => {
    const map = new Map();
    for (const f of picked) {
      try { map.set(f, URL.createObjectURL(f)); } catch (_) { /* not an image we can preview */ }
    }
    return map;
  }, [picked]);

  const closeUp = () => {
    for (const url of previews.values()) { try { URL.revokeObjectURL(url); } catch (_) { /* already gone */ } }
    onClose?.();
  };

  const takeFiles = async (fileList) => {
    // 2026-09-15 — a Windows drag of iPhone photos arrives as typeless HEICs,
    // which the old image/* filter dropped without a word, and Chrome could
    // not preview them anyway. They are converted to JPEG here (utils/
    // heicPhotos), keeping their dates, so the order and the thumbnails work.
    const candidates = Array.from(fileList || []).filter(f => f && mayBePhoto(f));
    if (!candidates.length) return;
    setError(null);
    setConverting(true);
    let result;
    try { result = await toBrowserImages(candidates); }
    finally { setConverting(false); }
    const files = result.files;
    if (result.failed.length) setError(`${result.failed.join(', ')} could not be read. Save ${result.failed.length === 1 ? 'it' : 'them'} as a JPEG and pick again.`);
    if (!files.length) return;
    setPicked(files);
    const r = assignInOrder(files, defects);
    setPairs(r.pairs);
    setExtraPhotos(r.extraPhotos);
    setUnphotographed(r.unphotographed);
  };

  const attach = async () => {
    const live = pairs.filter(p => p && p.defect && p.photo);
    if (!live.length) { setError('Nothing is assigned to an item yet.'); return; }
    setBusy(true);
    setProgress({ done: 0, total: live.length });
    let failed = 0;
    try {
      for (let i = 0; i < live.length; i += 1) {
        const { photo, defect } = live[i];
        setProgress({ done: i, total: live.length });
        try {
          // eslint-disable-next-line no-await-in-loop
          const v = await makePhotoBlobs(photo);
          // eslint-disable-next-line no-await-in-loop
          await addToOutbox('POST', '/api/defect-photos', {
            defectId: Number(defect.id),
            caption: photo.name || null,
            width: v.width || null,
            height: v.height || null,
          }, { blobs: { url: v.original, thumbnailUrl: v.thumbnail, displayUrl: v.display } });
        } catch (e) {
          // One unreadable file must not cost the other twenty-four.
          failed += 1;
        }
      }
      setProgress({ done: live.length, total: live.length });
      drainOutbox(); // v0.171.0 — a no-op in site mode until Sync now / Wi-Fi
      onDone?.({ queued: live.length - failed, failed });
      closeUp();
    } finally {
      setBusy(false);
    }
  };

  const plan = describePlan({ pairs, extraPhotos, unphotographed });

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !busy) closeUp(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 5000,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{
        background: theme.surface, color: theme.text,
        border: `1px solid ${theme.border}`, borderRadius: 10,
        width: 'min(880px, 100%)', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px', borderBottom: `1px solid ${theme.border}`,
        }}>
          <Images size={18} color={theme.brand} />
          <strong style={{ flex: 1 }}>Add photos to this report</strong>
          <button onClick={closeUp} disabled={busy} title="Close"
            style={{ background: 'none', border: 'none', cursor: busy ? 'not-allowed' : 'pointer', color: theme.text }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto' }}>
          <input
            ref={fileRef} type="file" accept="image/*" multiple
            style={{ display: 'none' }}
            onChange={(e) => takeFiles(e.target.files)}
          />

          {converting && (
            <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 8 }}>Preparing the photos…</div>
          )}
          {picked.length === 0 ? (
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); takeFiles(e.dataTransfer?.files); }}
              style={{
                padding: 28, textAlign: 'center', cursor: 'pointer',
                border: `2px dashed ${theme.border}`, borderRadius: 8,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Choose the photos from this inspection</div>
              <div style={{ fontSize: 12, color: theme.textMuted || theme.text, opacity: 0.7 }}>
                They land against items {defects[0]?.defectNumber ?? 1}–{defects[defects.length - 1]?.defectNumber ?? defects.length} in the order they were taken. You can change any of them before anything uploads.
              </div>
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                padding: '8px 10px', borderRadius: 6,
                background: theme.bg, fontSize: 12, fontWeight: 600,
              }}>
                <span style={{ flex: 1 }}>{plan}</span>
                <button style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}
                  disabled={busy} onClick={() => fileRef.current?.click()}>Choose different photos</button>
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10,
              }}>
                {pairs.map((p, i) => (
                  <div key={i} style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 6 }}>
                    <div style={{
                      height: 100, borderRadius: 4, overflow: 'hidden', background: theme.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
                    }}>
                      {previews.get(p.photo)
                        ? <img src={previews.get(p.photo)} alt={p.photo.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 11, opacity: 0.6 }}>no preview</span>}
                    </div>
                    <select
                      value={p.defect ? String(p.defect.id) : ''}
                      disabled={busy}
                      onChange={(e) => {
                        const d = defects.find(x => String(x.id) === e.target.value) || null;
                        setPairs(prev => repoint(prev, i, d));
                      }}
                      style={{
                        width: '100%', fontSize: 12, padding: '4px 6px',
                        border: `1px solid ${theme.border}`, borderRadius: 4,
                        background: theme.surface, color: theme.text,
                      }}
                    >
                      <option value="">— don't attach —</option>
                      {defects.map(d => (
                        <option key={d.id} value={String(d.id)}>
                          {d.defectNumber}. {String(d.description || '').slice(0, 40)}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {extraPhotos.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: theme.warn }}>
                  {extraPhotos.length} more photo{extraPhotos.length === 1 ? '' : 's'} than there are items — pick fewer, or attach these to an item by hand afterwards.
                </div>
              )}
              {unphotographed.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: theme.warn }}>
                  Still without a photo: {unphotographed.map(d => d.defectNumber).join(', ')}
                </div>
              )}
            </>
          )}

          {error && <div style={{ marginTop: 10, fontSize: 12, color: theme.danger }}>{error}</div>}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderTop: `1px solid ${theme.border}`,
        }}>
          <span style={{ flex: 1, fontSize: 12, opacity: 0.75 }}>
            {busy ? `Queuing ${progress.done + 1} of ${progress.total}…` : ''}
          </span>
          <button style={{ ...btnSecondary }} onClick={closeUp} disabled={busy}>Cancel</button>
          <button style={{ ...btnPrimary }} onClick={attach} disabled={busy || pairs.length === 0}>
            {busy ? 'Attaching…' : 'Attach photos'}
          </button>
        </div>
      </div>
    </div>
  );
}
